import { __, formatDate } from '../utils';
import {
  alpha,
  Box,
  ListItem,
  Stack,
  Typography,
} from '@presov.sk/presov-ui-react';
import React from 'react';

type Props = {
  transaction: {
    token_id: string;
    target: string;
    inserted_at?: string;
    expires_at?: string;
    revoked_at?: string;
  };
  onOpen: () => void;
};

const VotingTokenTransactionsListItem: React.FC<Props> = ({
  transaction,
  onOpen,
}) => {
  // „Teraz" sa číta raz, pri prvom vykreslení, nie pri každom. `Date.now()`
  // priamo v renderi by dal pri každom prekreslení iný výsledok (a React
  // Compiler to odmieta). Hlas, ktorý expiruje, kým je zoznam otvorený,
  // sa ako expirovaný ukáže po jeho ďalšom otvorení - to stačí.
  const [now] = React.useState(() => Date.now());

  // Štítok stavu - tichý podklad z tej istej farby, akou je napísaný text.
  const getStatus = () => {
    if (transaction.revoked_at) {
      return {
        value: 'cancelled' as const,
        label: __('Zrušený'),
        palette: 'error' as const,
      };
    }
    if (
      transaction.expires_at &&
      new Date(transaction.expires_at).getTime() < now
    ) {
      return {
        value: 'expired' as const,
        label: __('Expirovaný'),
        palette: 'neutral' as const,
      };
    }
    return {
      value: 'active' as const,
      label: __('Aktívny'),
      palette: 'success' as const,
    };
  };

  const status = getStatus();
  const isSpent = status.value !== 'active';

  return (
    <ListItem
      onClick={onOpen}
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: { xs: 0.5, sm: 1.5 },
        px: 2,
        py: 1.25,
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: (theme) => theme.palette.action.hover,
        },
      }}
    >
      <Typography
        variant='body2'
        color='text.secondary'
        sx={{
          fontFamily: 'monospace',
          fontWeight: 600,
          minWidth: { sm: '15ch' },
          textDecoration: isSpent ? 'line-through' : 'none',
        }}
      >
        {transaction.token_id}
      </Typography>

      <Typography
        variant='body2'
        sx={{
          flex: 1,
          minWidth: 0,
          fontWeight: 500,
          wordBreak: 'break-word',
        }}
      >
        {transaction.target}
      </Typography>

      <Stack
        direction='row'
        alignItems='center'
        gap={1.5}
        sx={{ flexShrink: 0 }}
      >
        {!!transaction.inserted_at && (
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
          >
            {formatDate(new Date(transaction.inserted_at))}
          </Typography>
        )}
        <Box
          sx={(theme) => {
            const color =
              status.palette === 'neutral'
                ? theme.palette.text.secondary
                : theme.palette[status.palette].dark;

            return {
              display: 'inline-flex',
              alignItems: 'center',
              height: 24,
              px: 1.25,
              borderRadius: 99,
              color,
              bgcolor:
                status.palette === 'neutral'
                  ? theme.palette.grey[100]
                  : alpha(color, 0.1),
              fontSize: 12.5,
              fontWeight: 700,
              whiteSpace: 'nowrap',
            };
          }}
        >
          {status.label}
        </Box>
      </Stack>
    </ListItem>
  );
};

export default VotingTokenTransactionsListItem;
